import { isValidElement, useCallback } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { FooterMode } from "@similarweb/ai-kit";

import { useFloatingFrameProps } from "app/components/ai-kit/FloatingFrameProvider";
import { translate } from "app/filters/i18nFilter";
import type { KeywordList } from "app/modules/demand-analysis/keyword-lists";
import { useKeywordLists } from "app/modules/demand-analysis/keyword-lists";
import { allTrackers } from "app/services/track/track";

import { DemandTrendsSpikeFeedContainer } from "../search-trends-page/common/DemandTrendsSpikeFeedContainer";

/**
 * Shared Trend Analyzer action used by both the in-chart tooltip spike button and the
 * widget header "Analyze" button, so both entry points open the same floating frame and
 * emit identical tracking for a given anomaly (spike) date.
 */
export const useDemandTrendsSpikeAnalysis = () => {
    const { setFrameProps, frameProps, isVisible } = useFloatingFrameProps();
    const { params } = useCurrentStateAndParams();
    const { keywordLists } = useKeywordLists();

    // The spike currently mounted in the floating frame (i.e. the one the agent is analyzing).
    const activeMatchedDate = isValidElement<{ matchedDate?: string }>(frameProps.children)
        ? frameProps.children.props.matchedDate
        : undefined;

    const initDemandTrendsFrame = useCallback(
        (matchedDate: string, keywordList: KeywordList, country: string) => {
            setFrameProps(
                (prev: any) => ({
                    ...prev,
                    config: {
                        ...prev.config,
                        className: "floating-frame",
                        headerProps: {
                            ...prev.config.headerProps,
                            title: translate("demand.trends.ai.agent.title"),
                            badge: translate("demand.trends.ai.agent.badge"),
                            topButtons: {
                                showClose: true,
                                showMinimize: true,
                            },
                        },
                        footerProps: {
                            footerMode: FooterMode.None,
                        },
                    },
                    showThread: false,
                    children: null,
                }),
                () => {
                    setFrameProps((prev: any) => ({
                        ...prev,
                        children: (
                            <DemandTrendsSpikeFeedContainer
                                matchedDate={matchedDate}
                                keywordListId={keywordList.keywordListId}
                                keywordListName={keywordList.name}
                                country={country}
                            />
                        ),
                    }));
                },
            );
        },
        [setFrameProps],
    );

    const analyzeSpike = useCallback(
        (anomalyDate: string) => {
            // Don't restart the agent when the frame is already open analyzing this same spike.
            if (isVisible && activeMatchedDate === anomalyDate) {
                return;
            }

            const keywordList = keywordLists[0];
            if (!keywordList) {
                return;
            }

            const { country } = params;
            allTrackers.dispatchTrackEvent(
                "demandTrendsGraphClick",
                "click",
                `demand.trends.graph.cluster/${keywordList.name}`,
                {
                    keywordListId: keywordList.keywordListId,
                    keywordListType: keywordList.type,
                    keywordListName: keywordList.name,
                    country,
                    matchedDate: anomalyDate,
                },
            );
            initDemandTrendsFrame(anomalyDate, keywordList, country);
        },
        [isVisible, activeMatchedDate, keywordLists, params, initDemandTrendsFrame],
    );

    return { analyzeSpike };
};
